/**
 * The calculator's location field, its pure parts (owner, 2026-10-26).
 *
 * Analysis 1800: "Yusif Vazir Chamazaminli 125" offered only Photon's
 * English street name with the number dropped; its point sat by Gold
 * Residence, the valuation snapped to that residence and named the area
 * Nəsimi, while the street is in Nərimanov. The field now offers our own
 * places and streets first, in our spelling; keeps a typed house number
 * (Photon places the house, or the street's middle is used and the field
 * says so); shows what the valuation will use under the input; and tells
 * the backend how the point was picked (location_source, see
 * backend services/calc_place.py).
 */

export type PlaceSource = "complex" | "place" | "street" | "address" | "pin";
type LineKey =
  | "sell.location.line.metro"
  | "sell.location.line.metro_only"
  | "sell.location.line.rayon"
  | "sell.location.line.near"
  | "sell.location.line.near_only";
type T = (key: LineKey) => string;

/** One row of GET /listings/suggest?valuation=1: a place or a street
 *  with the point it is valued at. */
export interface OurPlace {
  kind: "location" | "street";
  id: string;
  label: { az: string; ru: string; en: string };
  sub?: { az?: string; ru?: string; en?: string };
  match?: "exact" | "start" | "fuzzy";
  point: { lat: number; lng: number };
  /** Places: rayon | qesebe | metro | nisangah. */
  type?: string;
  /** Places: the rayon it is in. */
  rayon?: string | null;
  /** Streets: the street's own district and its "Settlement, Rayon" label. */
  district?: string | null;
  place?: string | null;
}

/** A residence of the catalogue (GET /complexes). */
export interface ResidenceEntry {
  name: string;
  lat: number;
  lng: number;
  district: string;
  tier: string;
  korter_slug: string;
  /** Catalogue id: the calculator sends it so the backend snaps to it. */
  cluster_id?: string;
  /** Alternate spellings ("28 Park Rezidens" for "28 Park Residence"). */
  aliases?: string[];
}

/** A Photon (komoot, OpenStreetMap) search feature. */
export interface PhotonFeature {
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    city?: string;
    district?: string;
    country?: string;
    osm_key?: string;
    osm_value?: string;
    type?: string;
  };
}

/** Fold Azerbaijani letters to ASCII so a plain-keyboard query matches:
 *  "qelebe" finds "Qələbə". NFD strips the decomposable diacritics
 *  (ö/ü/ğ/ş/ç + the İ→i̇ combining dot); ə and dotless ı don't decompose,
 *  so they're mapped explicitly. */
export function foldAz(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ə/g, "e")
    .replace(/ı/g, "i");
}

/** True when ``needle`` is an in-order subsequence of ``hay`` -
 *  "rensan" matches "renessans" (dropped letters). */
function isSubsequence(needle: string, hay: string): boolean {
  let i = 0;
  for (const ch of hay) {
    if (ch === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return i === needle.length;
}

/** Residences whose name or alias contains the query (folded), else,
 *  from four letters, the ones it is a subsequence of (shorter first). */
export function matchResidences(query: string, complexes: ResidenceEntry[], max = 6): ResidenceEntry[] {
  const q = foldAz(query.trim());
  if (!q) return [];
  const direct = complexes.filter(
    (c) => foldAz(c.name).includes(q) || (c.aliases ?? []).some((a) => foldAz(a).includes(q)),
  );
  if (direct.length > 0 || q.length < 4) return direct.slice(0, max);
  return complexes
    .filter((c) => isSubsequence(q, foldAz(c.name)) || (c.aliases ?? []).some((a) => isSubsequence(q, foldAz(a))))
    .sort((a, b) => a.name.length - b.name.length)
    .slice(0, max);
}

// A house number typed after the street: "Nizami 12", "Nizami küç., 12",
// "Nizami küçəsi ev 12", "Nizami 12A", "Nizami 12/5". The name before it
// must be words, not a bare number.
const HOUSE_NUMBER_RE =
  /^(.*?[\p{L}.)]\s*?)[\s,]+(?:(?:ev|bina|dom|д\.?|house|no\.?|№)\s*)?(\d{1,4}\s*[\p{L}]?(?:\s*[/-]\s*\d{1,4}\s*[\p{L}]?)?)\s*$/u;

/** "Yusif Vazir Chamazaminli 125" -> { text: "Yusif Vazir Chamazaminli",
 *  number: "125" }. A name that is only a number keeps it ("28 May" has
 *  no trailing number; "125" alone is not an address). */
export function splitHouseNumber(query: string): { text: string; number: string | null } {
  const q = query.replace(/\s+/g, " ").trim();
  const m = HOUSE_NUMBER_RE.exec(q);
  if (!m) return { text: q, number: null };
  const text = m[1].replace(/[\s,]+$/, "").trim();
  if (foldAz(text).replace(/[^a-zЀ-ӿ]/g, "").length < 3) return { text: q, number: null };
  return { text, number: m[2].replace(/\s+/g, "").toUpperCase() };
}

// Words that say the person means a residence or a street (folded).
const RESIDENCE_WORD_RE = /(^|\s)(residence|residences|rezidens\S*|rezidans|mtk|yk|жк|kompleks\S*|complex|tower|towers|plaza|комплекс\S*)(?=\s|$)/u;
const STREET_WORD_RE = /(^|\s)(kuc\S*|prospekt\S*|pr\.|donge\S*|kecid\S*|sosse\S*|xiyaban\S*|street|st\.|avenue|ave\.?|road|lane|улиц\S*|ул\.|проспект\S*|пр-т|переул\S*)(?=\s|$|,)/u;

export function namesResidence(query: string): boolean {
  return RESIDENCE_WORD_RE.test(foldAz(query));
}

export function namesStreet(query: string): boolean {
  return STREET_WORD_RE.test(foldAz(query));
}

/** One row of the field's panel. */
export type FieldOption =
  | { kind: "address"; street: OurPlace; number: string }
  | { kind: "place"; row: OurPlace }
  | { kind: "street"; row: OurPlace }
  | { kind: "residence"; c: ResidenceEntry }
  | { kind: "photon"; f: PhotonFeature };

/** Photon is asked only for an exact address our streets do not cover
 *  (a house number typed with no street of ours matching) and as the
 *  fallback when nothing of ours matches. */
export function photonWanted(input: { number: string | null; ours: OurPlace[]; residences: ResidenceEntry[] }): boolean {
  const ourStreet = input.ours.some((r) => r.kind === "street");
  return (input.number != null && !ourStreet) || (input.ours.length === 0 && input.residences.length === 0);
}

/**
 * The panel, in this order: a typed house number on each of our streets
 * (that is what an address is); our places (metro, settlement, rayon,
 * landmark); our streets; the residence catalogue; Photon last and only
 * when wanted (see photonWanted). A query that names its kind moves that
 * group up: "Gəncə prospekti" puts the streets first, "Port Baku
 * residence" the residences. Within a group the order is the search
 * service's (the typed name first, a station first after "metro").
 */
export function orderOptions(input: {
  query: string;
  number: string | null;
  ours: OurPlace[];
  residences: ResidenceEntry[];
  photon: PhotonFeature[];
}): FieldOption[] {
  const { number, ours, residences, photon } = input;
  const places: FieldOption[] = ours.filter((r) => r.kind === "location").map((row) => ({ kind: "place", row }));
  const streetRows = ours.filter((r) => r.kind === "street");
  const addresses: FieldOption[] = number ? streetRows.map((street) => ({ kind: "address", street, number })) : [];
  const streets: FieldOption[] = number ? [] : streetRows.map((row) => ({ kind: "street", row }));
  const res: FieldOption[] = residences.map((c) => ({ kind: "residence", c }));
  let groups: FieldOption[][];
  if (namesResidence(input.query)) groups = [res, addresses, places, streets];
  else if (namesStreet(input.query)) groups = [addresses, streets, places, res];
  else groups = [addresses, places, streets, res];
  const out = groups.flat();
  if (photonWanted({ number, ours, residences })) {
    const wanted = number ? normNumber(number) : null;
    const sorted = wanted
      ? [...photon].sort((a, b) => Number(normNumber(b.properties.housenumber) === wanted) - Number(normNumber(a.properties.housenumber) === wanted))
      : photon;
    out.push(...sorted.map((f): FieldOption => ({ kind: "photon", f })));
  }
  return out;
}

/** The panel's group of an option, for its section header. */
export function optionGroup(o: FieldOption): "house" | "places" | "streets" | "complexes" | "addresses" {
  switch (o.kind) {
    case "address": return "house";
    case "place": return "places";
    case "street": return "streets";
    case "residence": return "complexes";
    default: return "addresses";
  }
}

function normNumber(n: string | null | undefined): string {
  return (n ?? "").replace(/\s+/g, "").toUpperCase();
}

// Street designators, removed for a geocoder query and for comparing
// names ("Yusif Vəzir Çəmənzəminli küç." and "... küçəsi" are one street).
const DESIGNATOR_RE =
  /(^|\s)(küçəsi|küçə|küç\.?|kuc\.?|prospekti|prospekt|pr\.|döngəsi|döngə|keçidi|şossesi|xiyabanı|street|st\.|avenue|ave\.?|улица|ул\.|проспект)(?=\s|$|[.,])/giu;

/** The name part of a street: "Yusif Vəzir Çəmənzəminli küç." ->
 *  "Yusif Vəzir Çəmənzəminli". */
export function streetCore(name: string): string {
  return name.replace(DESIGNATOR_RE, " ").replace(/[.,]+/g, " ").replace(/\s+/g, " ").trim() || name.trim();
}

/** What to ask Photon for a house on one of our streets. */
export function houseQuery(streetName: string, number: string): string {
  return `${streetCore(streetName)} ${number}`;
}

/**
 * The house Photon found for "<street> <number>", or null. The number
 * must be the typed one, and the house on that street: its street name
 * (folded, without the designator) shares the name, or it stands within
 * ~1.5 km of the street's middle (OSM spells some streets differently).
 */
export function houseFromPhoton(
  features: PhotonFeature[],
  number: string,
  street: { name: string; point?: { lat: number; lng: number } | null },
): { lat: number; lng: number } | null {
  const wanted = normNumber(number);
  const core = foldAz(streetCore(street.name));
  for (const f of features) {
    if (normNumber(f.properties.housenumber) !== wanted) continue;
    const [lng, lat] = f.geometry.coordinates;
    const theirs = foldAz(streetCore(f.properties.street || f.properties.name || ""));
    const sameName = !!core && !!theirs && (theirs.includes(core) || core.includes(theirs));
    const near = street.point ? kmBetween(lat, lng, street.point.lat, street.point.lng) <= 1.5 : false;
    if (sameName || near) return { lat, lng };
  }
  return null;
}

function kmBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const x = ((bLng - aLng) * Math.PI / 180) * Math.cos(((aLat + bLat) / 2) * Math.PI / 180);
  const y = (bLat - aLat) * Math.PI / 180;
  return 6371 * Math.hypot(x, y);
}

/** What the valuation will use, shown quietly under the input. */
export interface Resolved {
  kind: "metro" | "settlement" | "rayon" | "landmark" | "street" | "address" | "residence" | "pin";
  /** The place, street, residence, or (a pin) the nearest place. */
  name: string;
  /** Its rayon; a street's "Settlement, Rayon" label. */
  area?: string | null;
  /** An address's house number. */
  number?: string | null;
  /** A pin the nearest endpoint names a residence for: the valuation
   *  snaps to it (the backend applies the same rule). */
  onResidence?: boolean;
}

/** The kind of a place row as the line words it. */
export function placeKind(type: string | undefined): Resolved["kind"] {
  if (type === "metro") return "metro";
  if (type === "rayon") return "rayon";
  if (type === "qesebe") return "settlement";
  return "landmark";
}

const fill = (s: string, name: string, area?: string | null) =>
  s.replace("{name}", name).replace("{area}", area ?? "");

/** "Yusif Vəzir Çəmənzəminli küç., Nərimanov"; "Şah İsmayıl Xətai
 *  metrosu, Xətai"; "Gold Residence, Nərimanov"; "Nərimanov, 8 Noyabr
 *  yaxınlığında". */
export function resolvedText(r: Resolved, t: T): string {
  const area = r.area && r.area !== r.name ? r.area : null;
  switch (r.kind) {
    case "metro":
      return fill(t(area ? "sell.location.line.metro" : "sell.location.line.metro_only"), r.name, area);
    case "rayon":
      return fill(t("sell.location.line.rayon"), r.name);
    case "address":
      return [r.number ? `${r.name} ${r.number}` : r.name, area].filter(Boolean).join(", ");
    case "pin":
      if (r.onResidence || !r.name) return [r.name || area, r.onResidence ? area : null].filter(Boolean).join(", ");
      return fill(t(area ? "sell.location.line.near" : "sell.location.line.near_only"), r.name, area);
    default:
      return [r.name, area].filter(Boolean).join(", ");
  }
}

/** The source to send for a location picked before sources existed
 *  (a value restored from elsewhere): a residence when one is named,
 *  else a point that never snaps to one. */
export function sourceOf(loc: { source?: PlaceSource | null; complex_korter_slug?: string | null; complex_slug?: string | null }): PlaceSource {
  if (loc.source) return loc.source;
  return loc.complex_slug || loc.complex_korter_slug ? "complex" : "address";
}
